import type {
  BoardFloorPlanData,
  FloorPlanConstraint,
  FloorPlanItem,
  FloorPlanMeasure,
  FloorPlanOpening,
  FloorPlanUnderlay,
  FloorPlanWall,
} from '../../../types';
import { clonePlan } from './floorPlanMath';

function jsonEq(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function planSignature(data: BoardFloorPlanData): string {
  return JSON.stringify({
    title: data.title || '',
    footprint: data.footprint,
    walls: data.walls,
    openings: data.openings,
    items: data.items,
    underlays: data.underlays || [],
    constraints: data.constraints || [],
    measures: data.measures || [],
  });
}

export type FloorPlanOmittedIds = Record<string, true>;

export function floorPlanEntityIds(data: BoardFloorPlanData): Record<string, true> {
  const ids: Record<string, true> = {};
  const add = (list?: Array<{ id: string }>) => {
    (list || []).forEach((entry) => {
      ids[entry.id] = true;
    });
  };
  add(data.walls);
  add(data.openings);
  add(data.items);
  add(data.underlays);
  add(data.constraints);
  add(data.measures);
  return ids;
}

export function applyOmittedDiff(
  omitted: FloorPlanOmittedIds,
  prev: BoardFloorPlanData,
  next: BoardFloorPlanData,
): FloorPlanOmittedIds {
  const out: FloorPlanOmittedIds = { ...omitted };
  const nextIds = floorPlanEntityIds(next);
  const markGone = (list?: Array<{ id: string }>) => {
    (list || []).forEach((entry) => {
      if (!nextIds[entry.id]) out[entry.id] = true;
    });
  };
  markGone(prev.walls);
  markGone(prev.openings);
  markGone(prev.items);
  markGone(prev.underlays);
  markGone(prev.constraints);
  markGone(prev.measures);
  Object.keys(out).forEach((id) => {
    if (nextIds[id]) delete out[id];
  });
  return out;
}

export function pruneConfirmedOmissions(
  omitted: FloorPlanOmittedIds,
  remote: BoardFloorPlanData,
): FloorPlanOmittedIds {
  const remoteIds = floorPlanEntityIds(remote);
  const out: FloorPlanOmittedIds = { ...omitted };
  Object.keys(out).forEach((id) => {
    if (!remoteIds[id]) delete out[id];
  });
  return out;
}

function collectEntities(data: BoardFloorPlanData): Record<string, unknown> {
  const map: Record<string, unknown> = {};
  const add = (list?: Array<{ id: string }>) => {
    (list || []).forEach((entry) => {
      map[entry.id] = entry;
    });
  };
  add(data.walls);
  add(data.openings);
  add(data.items);
  add(data.underlays);
  add(data.constraints);
  add(data.measures);
  return map;
}

export function ackDirtyIds(
  dirty: FloorPlanOmittedIds,
  local: BoardFloorPlanData,
  remote: BoardFloorPlanData,
): FloorPlanOmittedIds {
  const localMap = collectEntities(local);
  const remoteMap = collectEntities(remote);
  const out: FloorPlanOmittedIds = { ...dirty };
  Object.keys(out).forEach((id) => {
    if (jsonEq(localMap[id], remoteMap[id])) delete out[id];
  });
  return out;
}

export function dirtyEntityIds(base: BoardFloorPlanData, local: BoardFloorPlanData): FloorPlanOmittedIds {
  const dirty: FloorPlanOmittedIds = {};
  const mark = (baseList?: Array<{ id: string }>, localList?: Array<{ id: string }>) => {
    const bMap: Record<string, { id: string }> = {};
    (baseList || []).forEach((entry) => {
      bMap[entry.id] = entry;
    });
    (localList || []).forEach((entry) => {
      if (!jsonEq(entry, bMap[entry.id])) dirty[entry.id] = true;
    });
  };
  mark(base.walls, local.walls);
  mark(base.openings, local.openings);
  mark(base.items, local.items);
  mark(base.underlays, local.underlays);
  mark(base.constraints, local.constraints);
  mark(base.measures, local.measures);
  return dirty;
}

function pickEntity<T>(
  base: T | undefined,
  local: T | undefined,
  remote: T | undefined,
  omitted?: boolean,
  dirty?: boolean,
): T | null {
  if (omitted && !local) return null;
  if (dirty && local) return local;
  if (!local && !remote) return null;
  if (!local && remote) {
    if (!base) return remote;
    return jsonEq(remote, base) ? null : remote;
  }
  if (local && !remote) {
    if (!base) return local;
    return jsonEq(local, base) ? null : local;
  }
  if (jsonEq(local, base)) return remote as T;
  if (jsonEq(remote, base)) return local as T;
  return remote as T;
}

function mergeEntities<T extends { id: string }>(
  base: T[],
  local: T[],
  remote: T[],
  omitted: FloorPlanOmittedIds = {},
  dirty: FloorPlanOmittedIds = {},
): T[] {
  const bMap: Record<string, T> = {};
  const lMap: Record<string, T> = {};
  const rMap: Record<string, T> = {};
  base.forEach((entry) => {
    bMap[entry.id] = entry;
  });
  local.forEach((entry) => {
    lMap[entry.id] = entry;
  });
  remote.forEach((entry) => {
    rMap[entry.id] = entry;
  });

  const out: T[] = [];
  const used: Record<string, true> = {};
  const take = (id: string) => {
    if (used[id]) return;
    const next = pickEntity(bMap[id], lMap[id], rMap[id], omitted[id], dirty[id]);
    if (!next) return;
    used[id] = true;
    out.push(next);
  };

  remote.forEach((entry) => take(entry.id));
  local.forEach((entry) => take(entry.id));
  return out;
}

function mergeScalar<T>(base: T, local: T, remote: T, preferLocal?: boolean): T {
  if (preferLocal && !jsonEq(local, base)) return local;
  if (jsonEq(local, base)) return remote;
  if (jsonEq(remote, base)) return local;
  return remote;
}

/** 3-way merge so two people can add or move furniture without wiping each other. */
export function threeWayMergeFloorPlan(
  base: BoardFloorPlanData,
  local: BoardFloorPlanData,
  remote: BoardFloorPlanData,
  omitted: FloorPlanOmittedIds = {},
  dirty: FloorPlanOmittedIds = dirtyEntityIds(base, local),
): BoardFloorPlanData {
  const gone: FloorPlanOmittedIds = { ...omitted };
  Object.keys(applyOmittedDiff({}, base, local)).forEach((id) => {
    gone[id] = true;
  });
  return {
    title: mergeScalar(base.title || '', local.title || '', remote.title || '', !jsonEq(local.title || '', base.title || '')) || undefined,
    footprint: mergeScalar(base.footprint, local.footprint, remote.footprint, !jsonEq(local.footprint, base.footprint)),
    walls: mergeEntities<FloorPlanWall>(base.walls, local.walls, remote.walls, gone, dirty),
    openings: mergeEntities<FloorPlanOpening>(base.openings, local.openings, remote.openings, gone, dirty),
    items: mergeEntities<FloorPlanItem>(base.items, local.items, remote.items, gone, dirty),
    underlays: mergeEntities<FloorPlanUnderlay>(
      base.underlays || [],
      local.underlays || [],
      remote.underlays || [],
      gone,
      dirty,
    ),
    constraints: mergeEntities<FloorPlanConstraint>(
      base.constraints || [],
      local.constraints || [],
      remote.constraints || [],
      gone,
      dirty,
    ),
    measures: mergeEntities<FloorPlanMeasure>(
      base.measures || [],
      local.measures || [],
      remote.measures || [],
      gone,
      dirty,
    ),
  };
}

/** Put back local entities a stale remote snapshot dropped, if we still consider them dirty. */
export function restoreDroppedLocalEntities(
  merged: BoardFloorPlanData,
  local: BoardFloorPlanData,
  dirty: FloorPlanOmittedIds,
  omitted: FloorPlanOmittedIds = {},
): BoardFloorPlanData {
  const restore = <T extends { id: string }>(mergedList: T[], localList: T[]): T[] => {
    const have: Record<string, true> = {};
    mergedList.forEach((entry) => {
      have[entry.id] = true;
    });
    const extra: T[] = [];
    localList.forEach((entry) => {
      if (have[entry.id] || omitted[entry.id] || !dirty[entry.id]) return;
      extra.push(entry);
    });
    return extra.length ? mergedList.concat(extra) : mergedList;
  };
  return {
    ...merged,
    walls: restore(merged.walls, local.walls),
    openings: restore(merged.openings, local.openings),
    items: restore(merged.items, local.items),
    underlays: restore(merged.underlays || [], local.underlays || []),
    constraints: restore(merged.constraints || [], local.constraints || []),
    measures: restore(merged.measures || [], local.measures || []),
  };
}

/** True when live is missing ids the card already has — a stale or partial snapshot. */
export function liveDropsEntities(current: BoardFloorPlanData, live: BoardFloorPlanData): boolean {
  const currentIds = floorPlanEntityIds(current);
  const liveIds = floorPlanEntityIds(live);
  let lost = false;
  Object.keys(currentIds).forEach((id) => {
    if (!liveIds[id]) lost = true;
  });
  return lost;
}

export function floorPlanEntityCount(data?: BoardFloorPlanData | null): number {
  if (!data) return 0;
  return Object.keys(floorPlanEntityIds(data)).length;
}

/** Prefer the saved card when live is blank or missing geometry the card still has. */
export function preferSavedFloorPlan(
  saved?: BoardFloorPlanData | null,
  live?: BoardFloorPlanData | null,
): BoardFloorPlanData | undefined {
  if (!saved) return live || undefined;
  if (!live) return saved;
  if (liveDropsEntities(saved, live) && floorPlanEntityCount(live) < floorPlanEntityCount(saved)) {
    return saved;
  }
  return live;
}

export function overlayLiveFloorPlans<T extends { id: string; type?: string | null; data: unknown }>(
  nodes: T[],
  plans: Record<string, BoardFloorPlanData>,
  skipIds?: Record<string, true>,
): T[] {
  let changed = false;
  const next = nodes.map((node) => {
    if (node.type !== 'floorplan') return node;
    if (skipIds?.[node.id]) return node;
    const live = plans[node.id];
    if (!live) return node;
    const current = node.data as BoardFloorPlanData;
    if (planSignature(current) === planSignature(live)) return node;
    if (liveDropsEntities(current, live)) return node;
    changed = true;
    return { ...node, data: { ...(current as object), ...clonePlan(live) } };
  });
  return changed ? next : nodes;
}
